/* import __COLOCATED_TEMPLATE__ from './teammate-app-island.hbs'; */
/* RESPONSIBLE TEAM: team-frontend-tech */

/*
 * ⚠️  WARNING: BRIDGE LOGIC - DO NOT MODIFY WITHOUT #team-frontend-tech APPROVAL ⚠️
 *
 * This component contains critical bridge logic that enables communication between the
 * Ember app (apps/embercom) and React app (apps/teammate-app). Any modifications
 * to this component can break the integration between the two applications.
 *
 * Before making any changes to this file, please reach out to #team-frontend-tech
 * for review and approval.
 */

import Component from '@glimmer/component';
import type Owner from '@ember/owner';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import { registerDestructor } from '@ember/destroyable';
import { isReactRoute } from 'embercom/lib/react-route';
import { captureException } from 'embercom/lib/sentry';
import ENV from 'embercom/config/environment';

const BOOT_TIMEOUT = 30000;

interface Signature {
  Element: HTMLDivElement;
  Args: {};
}

export default class ReactTeammateAppIsland extends Component<Signature> {
  @tracked isLoaded = false;
  @tracked isError = false;
  @tracked bootTimeout: ReturnType<typeof setTimeout> | null = null;

  @service declare router: RouterService;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  constructor(owner: Owner, args: any) {
    super(owner, args);

    next(() => {
      if (isReactRoute(this.router.currentRoute)) {
        this.reactTeammateAppIsland.showReactContent();
      }
    });

    this.bootTimeout = setTimeout(() => {
      this.onError(new Error('React island boot timed out'));
    }, BOOT_TIMEOUT);

    registerDestructor(this, () => {
      this.clearBootTimeout();
    });

    if (!window.highlightReactComponents) {
      window.highlightReactComponents = () => {
        this.reactTeammateAppIsland.highlightReactComponents =
          !this.reactTeammateAppIsland.highlightReactComponents;
      };
    }

    registerDestructor(this, () => {
      window.highlightReactComponents = undefined;
      this.reactTeammateAppIsland.highlightReactComponents = false;
    });
  }

  get shouldHighlightReactComponents() {
    return this.reactTeammateAppIsland.highlightReactComponents;
  }

  get shouldShowReactDevError() {
    return ENV.environment === 'development';
  }

  clearBootTimeout() {
    if (this.bootTimeout) {
      clearTimeout(this.bootTimeout);
    }
  }

  @action
  onAppReady() {
    this.clearBootTimeout();
    this.isLoaded = true;
    this.reactTeammateAppIsland.onAppReady();
  }

  @action
  onError(error: Error) {
    this.clearBootTimeout();

    this.isLoaded = false;
    this.isError = true;
    captureException(error, {
      tags: {
        'react-teammate-app-island-error': true,
      },
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'React::TeammateAppIsland': typeof ReactTeammateAppIsland;
    'react/teammate-app-island': typeof ReactTeammateAppIsland;
  }
}
